// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, finalize, from, map, Observable, switchMap, take, tap } from 'rxjs';
import { HceEncryptionService } from '../hce-encryption/hce-encryption.service';
import { HceAssembleViewRequest } from '../../../interfaces/hce/hce-assemble-view';
import { Analysis } from '../../../interfaces/analysis';
import { HceService } from '../hce.service';
import { HceSvService } from '../hce-sv/hce-sv.service';
import { HceSvFilter } from '../../../interfaces/hce/hce-sv-filter';

@Injectable({ providedIn: 'root' })
export class HceAssembleViewService {

  // ===== Internal state (Observable-based) =====
  private readonly refsNameMappingSubject = new BehaviorSubject<Record<string, string> | null>(null);

  // ===== Accessors =====
  public get refsNameMapping(): Record<string, string> {
    return this.refsNameMappingSubject.value ?? {};
  }

  public get refsNameMapping$(): Observable<Record<string, string>> {
    return this.refsNameMappingSubject.pipe(
      filter((v): v is Record<string, string> => v !== null),
      take(1),
    );
  }

  // ===== Constructor =====
  constructor(
    private readonly hce: HceService,
    private readonly enc: HceEncryptionService,
    private readonly sv: HceSvService,
  ) {}

  // ===== Public API =====
  public getParams(token: string): Observable<HceAssembleViewRequest> {
    return from(this.enc.decrypt(token)).pipe(
      map(text => {
        if (text == null) {
          throw new Error('Invalid token: decrypted value is null');
        }
        let raw: unknown;
        try {
          raw = JSON.parse(text);
        } catch (e) {
          throw new Error('Invalid token: JSON parse failed');
        }
        const params = raw as HceAssembleViewRequest;
        if (!params.analysisId) {
          throw new Error('Invalid token: analysisId is missing');
        }
        return params;
      })
    );
  }

  public getAnalysis(analysisId: number): Observable<Analysis> {
    return this.hce.getAnalysis(analysisId).pipe(
      map(response => {
        if (!response || response.success === false) {
          throw new Error(response?.message || 'Error while fetching the analysis.');
        }
        return response.data as Analysis;
      }),
      switchMap(data => 
        this.hce.getRefsNameMapping(data.PanelID).pipe(
          tap(mapping => this.refsNameMappingSubject.next(mapping)),
          map(() => data)
        )
      ),
    );
  }

  public getAssembleData(analysisId: number, contigId: number): Observable<any> {
    console.time('Assemble data: download+transform');
    return this.hce.getAssembleJson(analysisId, contigId).pipe(
      finalize(() => console.timeEnd('Assemble data: download+transform'))
    );
  }

  public getSvData(analysis: Analysis, filter: HceSvFilter): Observable<any[]> {
    console.time('SV data: download+transform');
    return this.sv.getSvData(analysis, filter, {}, {
      useRefsNameMapping: true,
      useColorMap: true
    }).pipe(
      finalize(() => console.timeEnd('SV data: download+transform'))
    );
  }
}
